import { Size } from '~/types/ThemeTypes'
import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  StyleThemingProps,
} from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

export enum ExtensionBadgeVariant {
  Blue = 'Blue',
  Gray = 'MangoGray',
  LightGray = 'LightGray',
  White = 'White',
}

const extensionBadgeStyleParts = ['container', 'text'] as const
const configHelper = createMultiStyleConfigHelpers(extensionBadgeStyleParts)

export const ExtensionBadgeStyle = configHelper.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'inline-flex',
      flexShrink: '0',
      width: 'fit-content',
      borderColor: 'fg.info',
      border: '2px solid',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '3.25rem',
      backgroundColor: 'transparent',
      gap: '.5rem',
      userSelect: 'none',
    },
    text: {
      ...textStyles[textStyleKeys.Body2_Semibold],
      color: 'fg.info',
    },
  },
  variants: {
    [ExtensionBadgeVariant.Blue]: {
      container: {
        color: 'fg.info',
        backgroundColor: 'transparent',
      },
      text: {
        color: 'fg.info',
        ...textStyles[textStyleKeys.Body2_Semibold],
      },
    },
    [ExtensionBadgeVariant.Gray]: {
      container: {
        color: 'border.default',
        backgroundColor: 'transparent',
      },
      text: {
        color: 'fg.default',
        ...textStyles[textStyleKeys.Body2_Semibold],
      },
    },
    [ExtensionBadgeVariant.LightGray]: {
      container: {
        color: 'border.default',
        backgroundColor: 'transparent',
      },
      text: {
        color: 'fg.muted',
        ...textStyles[textStyleKeys.Body2_Semibold],
      },
    },
    [ExtensionBadgeVariant.White]: {
      container: {
        color: 'white',
        backgroundColor: 'transparent',
      },
      text: {
        color: 'white',
        ...textStyles[textStyleKeys.Body2_Semibold],
      },
    },
  },
  sizes: {
    [Size.sm]: {
      container: {
        borderRadius: '3.125rem',
        px: '.5rem',
        height: '1.125rem',
      },
      text: {
        ...textStyles[textStyleKeys.Body2_Semibold],
      },
    },
    [Size.md]: {
      container: {
        borderRadius: '50px',
        px: '.5rem',
        height: '1.5rem',
      },
      text: {
        ...textStyles[textStyleKeys.Body2_Semibold],
      },
    },
    [Size.lg]: {
      container: {
        borderRadius: '3.125rem',
        px: '.75rem',
        height: '1.75rem',
      },
      text: {
        ...textStyles[textStyleKeys.Body1_Bold],
      },
    },
    [Size.xl]: {
      container: {
        borderRadius: '3.125rem',
        px: '1.25rem',
        height: '2.5rem',
      },
      text: {
        ...textStyles[textStyleKeys.H5],
      },
    },
  },
  defaultProps: {
    size: Size.md,
    variant: ExtensionBadgeVariant.Blue,
  },
})

export const useExtensionBadgeStyles = (props?: StyleThemingProps) => {
  return mergeMultiStyleConfig(ExtensionBadgeStyle, props) as MultiStyleType<
    typeof extensionBadgeStyleParts
  >
}
